import type { Rating, RatingHistoryEntry, SeasonRating } from "../types/rating";
import type { FilmMetadata } from "../types/media";
import { isUnknownArray, isRecord, isTimestamp } from "../types/data";

export interface RatingsCacheData {
  ratings?: Rating[];
  ratingHistory?: RatingHistoryEntry[];
  historyTombstones?: RatingHistoryEntry[];
  filmMap?: Record<string, FilmMetadata>;
  version?: number;
  savedAt?: number;
  serverRevision?: number;
  lastServerTimestamp?: number;
  lastFullFetchAt?: number;
  lastSyncedAt?: number;
  serverCount?: number;
  lastCountCheckedAt?: number;
  historyLastServerTimestamp?: number;
  historyLastFullFetchAt?: number;
  historyLastSyncedAt?: number;
  historyServerCount?: number;
  posterRepairVersion?: number;
}
export const USER_RATINGS_CACHE_VERSION = 6;

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) &&
  typeof value === "object" &&
  !isUnknownArray(value);

const getFilmId = (entry: Pick<Rating, "filmId" | "id"> = {}) =>
  String(entry?.filmId || entry?.id || "").trim();

const toMillis = (value: unknown): number => {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (isRecord(value) && typeof value.toMillis === "function") return Number(value.toMillis());
  if (isTimestamp(value)) return value.toDate().getTime();
  if (isRecord(value) && Number.isFinite(Number(value.seconds))) {
    return (
      Number(value.seconds) * 1000 +
      Math.floor(Number(value.nanoseconds || 0) / 1000000)
    );
  }
  const parsed = typeof value === "string" || typeof value === "number" ? new Date(value) : new Date(NaN);
  const time = parsed.getTime();
  return Number.isFinite(time) ? time : 0;
};

export const reviveRatingsCacheDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (isTimestamp(value)) {
    const date = value.toDate();
    return Number.isNaN(date?.getTime?.()) ? null : date;
  }
  const milliseconds = toMillis(value);
  return milliseconds ? new Date(milliseconds) : null;
};

const reviveSeasonRatings = (seasonRatings: Record<string, SeasonRating> | null | undefined): Record<string, SeasonRating> | null => {
  if (!seasonRatings || typeof seasonRatings !== "object") {
    return seasonRatings || null;
  }

  return Object.fromEntries(
    Object.entries(seasonRatings).map(([seasonKey, seasonValue]) => [
      seasonKey,
      seasonValue && typeof seasonValue === "object"
        ? {
            ...seasonValue,
            ratedAt: reviveRatingsCacheDate(seasonValue.ratedAt)
          }
        : seasonValue
    ])
  );
};

const reviveRating = (rating: Rating = {}): Rating => ({
  ...rating,
  ratedAt: reviveRatingsCacheDate(rating.ratedAt),
  firstRatedAt: reviveRatingsCacheDate(rating.firstRatedAt),
  firstTrackedAt: reviveRatingsCacheDate(rating.firstTrackedAt),
  overallRatedAt: reviveRatingsCacheDate(rating.overallRatedAt),
  detailedRatedAt: reviveRatingsCacheDate(rating.detailedRatedAt),
  seasonRatingsUpdatedAt: reviveRatingsCacheDate(rating.seasonRatingsUpdatedAt),
  updatedAt: reviveRatingsCacheDate(rating.updatedAt),
  weeklyQuestMetadataUpdatedAt: reviveRatingsCacheDate(
    rating.weeklyQuestMetadataUpdatedAt
  ),
  seasonRatings: reviveSeasonRatings(rating.seasonRatings)
});

const reviveHistoryEntry = (entry: RatingHistoryEntry = {}): RatingHistoryEntry => ({
  ...reviveRating(entry),
  lastRatedAt: reviveRatingsCacheDate(entry.lastRatedAt),
  deletedAt: reviveRatingsCacheDate(entry.deletedAt)
});

const normalizeStoredEntries = (entries: Rating[] | undefined, reviveEntry: (entry: Rating) => Rating): (Rating & { filmId: string })[] =>
  (isUnknownArray(entries) ? entries : [])
    .filter(isPlainRecord)
    .map(reviveEntry)
    .map((entry): Rating & { filmId: string } => ({ ...entry, filmId: getFilmId(entry) }))
    .filter((entry) => Boolean(entry.filmId));

const normalizeStoredFilmMap = (filmMap: Record<string, FilmMetadata> | undefined): Record<string, FilmMetadata> =>
  isPlainRecord(filmMap) ? filmMap : {};

export const isRatingsHistoryTombstone = (entry: RatingHistoryEntry = {}) =>
  entry.active === false || Boolean(entry.deletedAt);

const hasUsefulValue = (value: unknown) => {
  if (value === undefined || value === null || value === "") return false;
  if (isUnknownArray(value)) return value.length > 0;
  if (typeof value === "object" && !(value instanceof Date)) {
    return Object.keys(value).length > 0;
  }
  return true;
};

const CANONICAL_CLEARABLE_RATING_FIELDS = new Set([
  "userRate",
  "detailedRate",
  "detailedAverage",
  "seasonRatings",
  "overallRatedAt",
  "detailedRatedAt",
  "seasonRatingsUpdatedAt"
]);

const mergeWithCanonicalValues = (fallback: Rating = {}, canonical: Rating = {}): Rating => {
  const merged = { ...fallback };
  const assignCanonicalField = <K extends keyof Rating>(key: K) => {
    const value = canonical[key];
    if (
      CANONICAL_CLEARABLE_RATING_FIELDS.has(key) ||
      hasUsefulValue(value) ||
      !hasUsefulValue(merged[key])
    ) merged[key] = value;
  };
  (Object.keys(canonical || {}) as (keyof Rating)[]).forEach(assignCanonicalField);
  return merged;
};

const mergeActiveHistoryIntoRatings = (ratings: Rating[] = [], history: RatingHistoryEntry[] = []) => {
  const byFilmId = new Map<string, Rating & { filmId: string }>();
  const order: string[] = [];

  ratings.forEach((rating: Rating) => {
    const filmId = getFilmId(rating);
    if (!filmId) return;
    if (!byFilmId.has(filmId)) order.push(filmId);
    byFilmId.set(filmId, { ...rating, filmId });
  });

  history.forEach((entry) => {
    if (isRatingsHistoryTombstone(entry)) return;
    const filmId = getFilmId(entry);
    if (!filmId) return;
    const rating = byFilmId.get(filmId);
    if (!rating) order.push(filmId);
    // XP settlement state is authoritative only in the history document. It
    // must never be folded into the cached active rating or a later season/date
    // edit may try to create the legacy marker under `/ratings/{filmId}`.
    const { experienceMutation: _historyOnlyMutation, ...ratingHistoryFields } = entry;
    // The rating document is canonical when the two copies disagree. Fields
    // that only existed in the old active-history copy (for example
    // lastRatedAt) are folded into that canonical entry before compaction.
    byFilmId.set(filmId, {
      ...mergeWithCanonicalValues(ratingHistoryFields, rating || {}),
      filmId
    });
  });

  return order.map((filmId) => byFilmId.get(filmId)).filter((rating): rating is Rating & { filmId: string } => rating !== undefined);
};

const foldFilmMetadataIntoEntries = (entries: Rating[] = [], filmMap: Record<string, FilmMetadata> = {}) =>
  entries.map((entry) => {
    const filmId = getFilmId(entry);
    const metadata = filmId && filmMap?.[filmId] && typeof filmMap[filmId] === "object"
      ? filmMap[filmId]
      : {};
    // Keep a useful fallback from filmMap when a legacy rating contains only
    // an empty placeholder. Otherwise the canonical rating/history value wins.
    return { ...mergeWithCanonicalValues(metadata, entry), filmId };
  });

const getEntryUpdatedAt = (entry: Rating = {}) =>
  toMillis(entry.updatedAt || entry.lastRatedAt || entry.ratedAt);

const getDeletionUpdatedAt = (entry: RatingHistoryEntry = {}) =>
  toMillis(entry.updatedAt || entry.deletedAt || entry.lastRatedAt || entry.ratedAt);

const isSameRatingLifecycle = (activeEntry: Rating = {}, historyEntry: RatingHistoryEntry = {}) => {
  const activeLifecycleId = String(activeEntry?.ratingLifecycleId || "");
  const historyLifecycleId = String(historyEntry?.ratingLifecycleId || "");

  // Legacy records did not have lifecycle IDs, so timestamp ordering remains
  // the only safe signal for those entries. Once both IDs exist, a deletion
  // from an older lifecycle must never hide a later re-watch of the same film.
  return (
    !activeLifecycleId ||
    !historyLifecycleId ||
    activeLifecycleId === historyLifecycleId
  );
};

const keepEffectiveTombstones = (ratings: Rating[] = [], history: RatingHistoryEntry[] = []) => {
  const activeByFilmId = new Map(
    ratings
      .map((entry): [string, Rating] => [getFilmId(entry), entry])
      .filter(([filmId]) => Boolean(filmId))
  );

  return history.filter((entry) => {
    if (!isRatingsHistoryTombstone(entry)) return false;
    const activeEntry = activeByFilmId.get(getFilmId(entry));
    if (!activeEntry) return true;
    if (!isSameRatingLifecycle(activeEntry, entry)) return false;
    const deletedUpdatedAt = getDeletionUpdatedAt(entry);
    return Boolean(deletedUpdatedAt) && getEntryUpdatedAt(activeEntry) < deletedUpdatedAt;
  });
};

export const reconcileRatingsWithHistoryTombstones = (
  ratings: Rating[] = [],
  historyEntries: RatingHistoryEntry[] = []
) => {
  const effectiveTombstones = keepEffectiveTombstones(ratings, historyEntries);
  if (!effectiveTombstones.length) return ratings;
  const deletedFilmIds = new Set(effectiveTombstones.map(getFilmId));
  const nextRatings = ratings.filter(
    (rating: Rating) => !deletedFilmIds.has(getFilmId(rating))
  );
  return nextRatings.length === ratings.length ? ratings : nextRatings;
};

const mergeRatingsWithTombstones = (ratings: Rating[] = [], tombstones: RatingHistoryEntry[] = []) => {
  const byFilmId = new Map<string, RatingHistoryEntry>();
  ratings.forEach((rating: Rating) => {
    const filmId = getFilmId(rating);
    if (!filmId) return;
    byFilmId.set(filmId, {
      ...rating,
      filmId,
      active: rating.active !== false,
      lastRatedAt: rating.lastRatedAt || rating.ratedAt || null,
      deletedAt: null
    });
  });

  tombstones.forEach((entry) => {
    const filmId = getFilmId(entry);
    if (!filmId) return;
    const activeEntry = byFilmId.get(filmId);
    if (activeEntry) {
      if (!isSameRatingLifecycle(activeEntry, entry)) return;
      const activeUpdatedAt = getEntryUpdatedAt(activeEntry);
      const deletedUpdatedAt = getDeletionUpdatedAt(entry);
      if (!deletedUpdatedAt || activeUpdatedAt >= deletedUpdatedAt) return;
    }
    byFilmId.set(filmId, { ...(activeEntry || {}), ...entry, filmId });
  });

  return [...byFilmId.values()];
};

const compactFilmMap = (filmMap: Record<string, FilmMetadata> = {}, ratings: Rating[] = [], tombstones: RatingHistoryEntry[] = []) => {
  const representedIds = new Set();
  [...ratings, ...tombstones].forEach((entry) => {
    const filmId = getFilmId(entry);
    if (filmId) representedIds.add(filmId);
  });

  return Object.entries(filmMap || {}).reduce<Record<string, FilmMetadata>>((result, [rawFilmId, metadata]) => {
    if (!metadata || typeof metadata !== "object") return result;
    const filmId = String(rawFilmId);
    // Before this runs, every useful fallback field is folded into its
    // canonical rating/tombstone. Only metadata for IDs with no represented
    // entry needs a separate durable copy.
    if (!representedIds.has(filmId)) {
      result[filmId] = metadata;
    }
    return result;
  }, {});
};

const expandFilmMap = (compactFilmMapValue: Record<string, FilmMetadata> = {}, ratings: Rating[] = [], history: RatingHistoryEntry[] = []) => {
  const expanded: Record<string, FilmMetadata> = {};
  [...ratings, ...history].forEach((entry) => {
    const filmId = getFilmId(entry);
    if (!filmId) return;
    expanded[filmId] = { ...(expanded[filmId] || {}), ...entry, filmId };
  });
  Object.entries(compactFilmMapValue || {}).forEach(([filmId, metadata]) => {
    expanded[filmId] = {
      ...(expanded[filmId] || {}),
      ...(metadata || {}),
      filmId
    };
  });
  return expanded;
};

/**
 * Converts the in-memory shape into the durable v6 shape. Components still
 * receive a complete ratingHistory; only IndexedDB stores tombstones because
 * every active history entry can be reconstructed from ratings.
 */
export const buildCompactRatingsCacheData = (data: RatingsCacheData = {}) => {
  const runtimeRatings = foldFilmMetadataIntoEntries(
    isUnknownArray(data.ratings) ? data.ratings : [],
    data.filmMap
  );
  const runtimeHistory = foldFilmMetadataIntoEntries(
    isUnknownArray(data.ratingHistory)
    ? data.ratingHistory
    : isUnknownArray(data.historyTombstones)
      ? data.historyTombstones
      : [],
    data.filmMap
  );
  const mergedRatings = mergeActiveHistoryIntoRatings(runtimeRatings, runtimeHistory);
  const historyTombstones = keepEffectiveTombstones(mergedRatings, runtimeHistory)
    .map((entry) => ({ ...entry, filmId: getFilmId(entry) }))
    .filter((entry) => Boolean(entry.filmId));
  const ratings = reconcileRatingsWithHistoryTombstones(
    mergedRatings,
    historyTombstones
  );
  const filmMap = compactFilmMap(data.filmMap, ratings, historyTombstones);
  const { ratingHistory, ...rest } = data;

  return {
    ...rest,
    ratings,
    historyTombstones,
    filmMap,
    version: USER_RATINGS_CACHE_VERSION
  };
};

/**
 * Expands both legacy v5 and compact v6 records into the unchanged runtime
 * shape expected by UserRatingsContext.
 */
export const hydrateRatingsCacheData = (cachedData: RatingsCacheData = {}) => {
  const revivedRatings = normalizeStoredEntries(
    cachedData.ratings,
    reviveRating
  );
  const storedHistory = isUnknownArray(cachedData.historyTombstones)
    ? cachedData.historyTombstones
    : isUnknownArray(cachedData.ratingHistory)
      ? cachedData.ratingHistory
      : [];
  const revivedHistory = normalizeStoredEntries(
    storedHistory,
    reviveHistoryEntry
  );
  const mergedRatings = mergeActiveHistoryIntoRatings(revivedRatings, revivedHistory);
  const historyTombstones = revivedHistory.filter(isRatingsHistoryTombstone);
  const ratings = reconcileRatingsWithHistoryTombstones(
    mergedRatings,
    historyTombstones
  );
  const ratingHistory = mergeRatingsWithTombstones(ratings, historyTombstones);
  const savedAt = Number(cachedData.savedAt) || 0;
  const hasStoredHistory =
    isUnknownArray(cachedData.historyTombstones) ||
    isUnknownArray(cachedData.ratingHistory);
  const cachedServerCount = Number(cachedData.serverCount);
  const cachedHistoryServerCount = Number(cachedData.historyServerCount);

  return {
    ...cachedData,
    version: USER_RATINGS_CACHE_VERSION,
    ratings,
    ratingHistory,
    historyTombstones,
    filmMap: expandFilmMap(
      normalizeStoredFilmMap(cachedData.filmMap),
      ratings,
      historyTombstones
    ),
    lastServerTimestamp:
      Number(cachedData.lastServerTimestamp) ||
      ratings.reduce(
        (latest, rating: Rating) => Math.max(latest, toMillis(rating.updatedAt || rating.ratedAt)),
        0
      ),
    lastFullFetchAt: Number(cachedData.lastFullFetchAt) || savedAt,
    lastSyncedAt: Number(cachedData.lastSyncedAt) || savedAt,
    serverCount: Number.isFinite(cachedServerCount)
      ? cachedServerCount
      : ratings.length,
    lastCountCheckedAt: Number(cachedData.lastCountCheckedAt) || 0,
    historyLastServerTimestamp:
      Number(cachedData.historyLastServerTimestamp) ||
      revivedHistory.reduce(
        (latest, entry) => Math.max(latest, toMillis(entry.updatedAt || entry.ratedAt)),
        0
      ),
    historyLastFullFetchAt:
      Number(cachedData.historyLastFullFetchAt) || (hasStoredHistory ? savedAt : 0),
    historyLastSyncedAt:
      Number(cachedData.historyLastSyncedAt) || (hasStoredHistory ? savedAt : 0),
    historyServerCount: Number.isFinite(cachedHistoryServerCount)
      ? cachedHistoryServerCount
      : ratingHistory.length,
    posterRepairVersion: Number(cachedData.posterRepairVersion) || 0
  };
};
